import React, { useMemo } from 'react';
import { FieldValues, Control, Path, FieldError, useController } from 'react-hook-form';
import {
  Autocomplete,
  TextField,
  createFilterOptions,
  Chip,
  Skeleton,
  Checkbox,
  Box,
  Typography,
  useTheme,
} from '@mui/material';

import InputLabelText from '/packages/shared-components/forms/InputLabelText/InputLabelText';
import { ErrorMessage } from '/packages/shared-components/ErrorMessage/ErrorMessage';
import { getErrorMessage } from '../Input/ValidationErrors';
import { getChipStyles } from '../../StatusChip/StatusChip';

// Design System Component: Input Select > Chip
interface ValidateObject {
  [key: string]: (value: string | string[] | null) => boolean | string;
}

type Option = { label: string; value: string; subLabel?: string };

export interface FreeTextSelectProps<T extends FieldValues = FieldValues> {
  control: Control<T>;
  name: Path<T>;
  options: string[] | Option[];
  multiple?: boolean;
  addNewOptionMessage?: string;
  label: string;
  placeholder?: string;
  showModified?: boolean;
  required?: boolean;
  freeSolo?: boolean;
  validate?: ValidateObject;
  loading?: boolean;
  fetchError?: Error;
  selectedOnTop?: boolean;
  chipSize?: 'small' | 'medium';
  adjustStyle?: boolean;
  hideErrorMsg?: boolean;
  disabled?: boolean;
}

export interface FilterOptionsState<T> {
  inputValue: string;
  getOptionLabel: (option: T) => string;
}

const filter = createFilterOptions<Option>({
  stringify: (opt) => opt.label.concat(opt.subLabel ? ` ${opt.subLabel}` : ''),
});

const mapToOptionType = (arr: string[]) =>
  arr.map((item) => {
    const obj = {
      label: item,
      value: item,
    };
    return obj as Option;
  });

const getValue = (val: Option | string) => {
  if (typeof val === 'string') return val;
  return val.value;
};

const AutocompleteInput = <T extends FieldValues>({
  control,
  name,
  options,
  multiple = false,
  addNewOptionMessage,
  label,
  placeholder = '',
  showModified = false,
  required = false,
  freeSolo = false,
  validate,
  loading = false,
  selectedOnTop = false,
  fetchError,
  chipSize = 'medium',
  adjustStyle = false,
  hideErrorMsg = false,
  disabled = false,
}: FreeTextSelectProps<T>) => {
  const theme = useTheme();
  const optionsMap = useMemo(() => {
    const onlyStrings = typeof options[0] === 'string';
    return onlyStrings ? mapToOptionType(options as string[]) : (options as Option[]);
  }, [options]);

  const {
    field: { onChange, value },
    fieldState,
  } = useController({ control, name, rules: { required, validate } });

  const isModified = showModified && fieldState.isDirty;

  const error = fieldState.error as FieldError;
  const errorMessage = getErrorMessage(error);

  const handleChange = (_: unknown, value: string | string[] | null) => {
    onChange(value);
  };

  const handleOnOpen = () => {
    if (selectedOnTop && multiple)
      optionsMap.sort((a, b) => {
        const aSelected = value.includes(a.label);
        const bSelected = value.includes(b.label);
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        return 0;
      });
  };

  const handleOnClose = () => {
    if (selectedOnTop && multiple)
      optionsMap.sort((a, b) => {
        return a.label < b.label ? -1 : 1;
      });
  };

  const autocompleteValue = value !== undefined ? value : multiple ? [] : null;

  return (
    <InputLabelText
      label={label}
      name={name}
      required={required}
      errorMessage={errorMessage}
      hideLabel={false}
      showModified={isModified}
      hideErrorMsg={hideErrorMsg}
    >
      {loading ? (
        <Skeleton width="100%" height="64px" />
      ) : fetchError ? (
        <ErrorMessage errorMsg={fetchError.message} />
      ) : (
        <Autocomplete
          value={autocompleteValue}
          onChange={handleChange}
          onOpen={handleOnOpen}
          onClose={handleOnClose}
          sx={{ backgroundColor: 'background.paper' }}
          multiple={multiple}
          freeSolo={freeSolo}
          disabled={disabled}
          forcePopupIcon={true}
          disableCloseOnSelect={multiple}
          id={name}
          renderInput={(params) => (
            <TextField
              {...params}
              name={name}
              variant="outlined"
              placeholder={!value || value.length === 0 ? placeholder : ''}
              sx={{
                '& input::placeholder': {
                  color: 'text.secondary',
                  opacity: 1,
                },
                ...(adjustStyle && {
                  '& .MuiInputBase-root': {
                    height: '40px',
                  },
                }),
              }}
              slotProps={{ inputLabel: { shrink: true } }}
              error={!!errorMessage}
            />
          )}
          options={optionsMap.map((option) => option.label)}
          renderOption={({ key, ...props }, option, { selected }) => {
            const findLabel = optionsMap.find((w) => w.value === option);
            return findLabel !== undefined ? (
              <li style={{ height: 'auto', minHeight: '50px' }} key={`ro-${key}`} {...props}>
                {multiple && <Checkbox checked={selected} color="primary" />}
                <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, maxWidth: '100%' }}>
                  <Typography
                    component="span"
                    sx={{
                      wordBreak: 'break-word',
                      display: '-webkit-box',
                      overflow: 'hidden',
                      boxOrient: 'vertical',
                      lineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 2,
                    }}
                  >
                    {findLabel.label}
                  </Typography>
                  {findLabel.subLabel && (
                    <span
                      style={{
                        fontSize: '12px',
                        color: theme.palette.text.secondary,
                        marginTop: '2px',
                        maxWidth: '100%',
                        wordBreak: 'break-word',
                      }}
                    >
                      {findLabel.subLabel}
                    </span>
                  )}
                </div>
              </li>
            ) : (
              <Box
                component="li"
                key={`ro-${key}`}
                {...props}
                sx={{
                  height: '50px',
                  maxWidth: '100%',
                  color: 'text.primary !important',
                  ...(adjustStyle && {
                    '&[aria-disabled="true"]': {
                      color: 'text.primary !important',
                      opacity: '1 !important',
                    },
                  }),
                }}
              >
                {`${freeSolo ? 'Press Enter to create a new label' : 'No matches found for your search'}`}
              </Box>
            );
          }}
          isOptionEqualToValue={(option, value) => getValue(option) === getValue(value)}
          renderTags={(value, getTagProps) =>
            value.map((el, index) => {
              const findLabel = optionsMap.find((w) => w.value === el);
              return (
                <Chip
                  {...getTagProps({ index })}
                  sx={(theme) => ({
                    ...getChipStyles('primary', 'subtle', theme.palette),
                    border: `1px solid ${theme.palette.divider}`,
                    '& .MuiChip-deleteIcon': {
                      color: theme.palette.primary.main,
                    },
                  })}
                  label={findLabel?.label || el}
                  key={`${name}-tag-${index}`}
                  size={chipSize || 'medium'}
                />
              );
            })
          }
          getOptionDisabled={(option) => option === addNewOptionMessage || option === 'No options'}
          filterOptions={(_, params) => {
            const optionsFiltered = filter(optionsMap, {
              inputValue: params.inputValue,
              getOptionLabel: (option) => option.label,
            } as FilterOptionsState<Option>);
            const filtered = [];
            if (optionsFiltered.length) {
              filtered.push(...optionsFiltered.map((wf) => wf.value));
            } else {
              filtered.push(addNewOptionMessage || 'No options');
            }
            return filtered;
          }}
        />
      )}
    </InputLabelText>
  );
};

export default AutocompleteInput;
